import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Paper,
  TextField,
  useTheme,
  useMediaQuery,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { removeFromCart, updateQuantity } from '../../store/cartSlice';
import { getLotSize, formatLotQuantity } from '../../utils/lotSize';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const CartItems: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, total } = useSelector((state: RootState) => state.cart);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [itemToRemove, setItemToRemove] = useState<string | null>(null);

  const handleRemoveFromCart = (productId: string) => {
    setItemToRemove(productId);
  };

  const confirmRemove = () => {
    if (itemToRemove) {
      dispatch(removeFromCart(itemToRemove));
      setItemToRemove(null);
    }
  };

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity >= 1) {
      try {
        dispatch(updateQuantity({ productId, quantity: newQuantity }));
      } catch (error: any) {
        alert(error.message);
      }
    } else {
      setItemToRemove(productId);
    }
  };

  const handleCheckout = () => {
    navigate('/checkout/review');
  };

  return (
    <Box>
      {/* 🔔 Messages d'information selon les prix */}
      {items.some(item => getLotSize(item.product.price, item.product.lotSize) > 1) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Information :</strong> La quantité correspond au nombre de lots.
          La taille de chaque lot est indiquée sur l’article.
        </Alert>
      )}

      {/* 🛒 Liste des produits du panier */}
      {items.map((item) => {
        const price = item.product.price;
        const lotSize = getLotSize(price, item.product.lotSize);
        const isLot = lotSize > 1;
        const quantityLabel = isLot ? `(${formatLotQuantity(item.quantity, lotSize)})` : '';
        const totalPrice = price * item.quantity * lotSize;

        return (
          <Paper 
            key={item.product._id}
            elevation={0}
            sx={{ 
              p: 2, 
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              '&:last-child': { mb: 0 }
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {/* 🖼 Image du produit */}
              <Grid item xs={12} sm={2}>
                <Box
                  component="img"
                  src={item.product.images[0]}
                  alt={item.product.name}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 1,
                    objectFit: 'cover'
                  }}
                />
              </Grid>

              {/* 🧾 Infos produit */}
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {item.product.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.product.description}
                </Typography>
              </Grid>

              {/* 🔢 Quantité */}
              <Grid item xs={12} sm={3}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: isMobile ? 'flex-start' : 'center',
                  gap: 1 
                }}>
                  <IconButton
                    onClick={() => handleQuantityChange(item.product._id, item.quantity - 1)}
                    size="small"
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  
                  <TextField
                    value={item.quantity}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value)) {
                        handleQuantityChange(item.product._id, value);
                      }
                    }}
                    type="number"
                    size="small"
                    inputProps={{ 
                      min: 1,
                      style: { 
                        textAlign: 'center',
                        width: '40px',
                        padding: '4px'
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'divider',
                        },
                      }
                    }}
                  />

                  <IconButton
                    onClick={() => handleQuantityChange(item.product._id, item.quantity + 1)}
                    size="small"
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>

              {/* 💰 Prix total */}
              <Grid item xs={12} sm={3}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: isMobile ? 'flex-start' : 'flex-end',
                  alignItems: 'center' 
                }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {totalPrice.toLocaleString('fr-FR')} FCFA
                  </Typography>
                  {isLot && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {quantityLabel}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* 🗑 Supprimer */}
              <Grid item xs={12} sm={1}>
                <IconButton 
                  onClick={() => handleRemoveFromCart(item.product._id)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        );
      })}
      
      {/* 🧾 Total global */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Total: {total.toLocaleString('fr-FR')} FCFA
          </Typography>
        </Box>
      </Paper>
      <Dialog open={Boolean(itemToRemove)} onClose={() => setItemToRemove(null)}>
        <DialogTitle>Retirer ce produit du panier ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette action enlèvera le produit de votre panier.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemToRemove(null)}>Annuler</Button>
          <Button color="error" onClick={confirmRemove}>
            Retirer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CartItems;
